import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EnvelopeSimple, Megaphone, Eye, EyeSlash, Warning, CheckCircle } from "@phosphor-icons/react";

const PROFILI = [
  {
    key: "comunicazioni",
    titolo: "SMTP_Comunicazioni",
    icona: EnvelopeSimple,
    colore: "text-[#D95D39]",
    descrizione: "Tutte le comunicazioni di servizio: backup settimanale, ordini fornitore e notifiche di sistema.",
  },
  {
    key: "marketing",
    titolo: "SMTP_Marketing",
    icona: Megaphone,
    colore: "text-amber-600",
    descrizione: "Invio di comunicazioni marketing e newsletter. Configurazione separata per proteggere la reputazione dell'SMTP di servizio.",
  },
];

const emptyForm = { host: "", porta: 587, username: "", password: "", mittente: "", destinatari: "", usa_tls: true, invii_automatici: true };

export default function Impostazioni() {
  const { user } = useAuth();
  const [configs, setConfigs] = useState({});
  const [openProfilo, setOpenProfilo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const isMaster = user?.role === "master";

  const load = () => {
    Promise.all(PROFILI.map((p) => api.get(`/smtp/${p.key}`).then((r) => [p.key, r.data])))
      .then((entries) => setConfigs(Object.fromEntries(entries)))
      .catch(() => {});
  };
  useEffect(() => { if (isMaster) load(); }, [user]);

  if (!isMaster)
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="impostazioni-forbidden">
        <p className="font-semibold">Accesso riservato al ruolo Master.</p>
      </div>
    );

  const openConfig = (key) => {
    const c = configs[key] || {};
    setForm({
      host: c.host || "", porta: c.porta || 587, username: c.username || "", password: "",
      mittente: c.mittente || "", destinatari: c.destinatari || "",
      usa_tls: c.usa_tls !== false, invii_automatici: c.invii_automatici !== false,
    });
    setShowPwd(false);
    setOpenProfilo(key);
  };

  const save = async (silent = false) => {
    const payload = { ...form, porta: Number(form.porta) || 587 };
    if (!payload.password) delete payload.password;
    const { data } = await api.put(`/smtp/${openProfilo}`, payload);
    setConfigs((c) => ({ ...c, [openProfilo]: data }));
    if (!silent) toast.success("Impostazioni SMTP salvate");
  };

  const onSave = async () => {
    setBusy(true);
    try { await save(); setOpenProfilo(null); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      await save(true);
      const { data } = await api.post(`/smtp/${openProfilo}/test`, { destinatario: form.destinatari });
      toast.success(data.message);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const profiloAttivo = PROFILI.find((p) => p.key === openProfilo);

  return (
    <div className="space-y-6 fade-in" data-testid="impostazioni-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Configurazione</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-1">Impostazioni</h1>
        <p className="text-[#6B7280] mt-1">Gestione centralizzata delle configurazioni SMTP. Clicca su una scheda per configurare.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {PROFILI.map((p) => {
          const cfg = configs[p.key] || {};
          return (
            <button
              key={p.key}
              onClick={() => openConfig(p.key)}
              data-testid={`smtp-card-${p.key}`}
              className="text-left bg-white border border-[#E5E5E0] rounded-2xl p-8 hover:border-[#D95D39] transition-colors"
            >
              <p.icona size={40} weight="duotone" className={p.colore} />
              <h2 className="text-lg font-bold mt-4">{p.titolo}</h2>
              <p className="text-sm text-[#6B7280] mt-2">{p.descrizione}</p>
              <div className="mt-4 flex items-center gap-2 text-sm font-medium" data-testid={`smtp-status-${p.key}`}>
                {cfg.configurato ? (
                  <><CheckCircle size={18} weight="fill" className="text-green-600" /><span className="text-green-700">Configurato — {cfg.host}</span></>
                ) : (
                  <><Warning size={18} weight="fill" className="text-amber-500" /><span className="text-amber-600">Non configurato — clicca per configurare</span></>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!openProfilo} onOpenChange={(v) => !v && setOpenProfilo(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="smtp-dialog">
          {profiloAttivo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <profiloAttivo.icona size={22} weight="duotone" className={profiloAttivo.colore} /> {profiloAttivo.titolo}
                </DialogTitle>
                <DialogDescription>{profiloAttivo.descrizione}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Host SMTP</Label>
                    <Input value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} placeholder="smtp.esempio.it" data-testid="smtp-host-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Porta</Label>
                    <Input type="number" value={form.porta} onChange={(e) => setForm((f) => ({ ...f, porta: e.target.value }))} data-testid="smtp-porta-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Username</Label>
                    <Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="utente@esempio.it" data-testid="smtp-username-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password {configs[openProfilo]?.password_impostata && <span className="text-xs text-[#6B7280]">(salvata — lascia vuoto per non cambiare)</span>}</Label>
                    <div className="relative">
                      <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="smtp-password-input" className="pr-10" />
                      <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="smtp-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
                        {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Mittente (From)</Label>
                    <Input value={form.mittente} onChange={(e) => setForm((f) => ({ ...f, mittente: e.target.value }))} placeholder="info@risorice.it" data-testid="smtp-mittente-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Destinatari</Label>
                    <Input value={form.destinatari} onChange={(e) => setForm((f) => ({ ...f, destinatari: e.target.value }))} placeholder="a@x.it, b@x.it" data-testid="smtp-destinatari-input" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.usa_tls} onCheckedChange={(v) => setForm((f) => ({ ...f, usa_tls: !!v }))} data-testid="smtp-tls-checkbox" id="smtp-tls" />
                  <Label htmlFor="smtp-tls" className="font-normal">Usa TLS (STARTTLS; per porta 465 viene usato SSL)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.invii_automatici} onCheckedChange={(v) => setForm((f) => ({ ...f, invii_automatici: !!v }))} data-testid="smtp-auto-checkbox" id="smtp-auto" />
                  <Label htmlFor="smtp-auto" className="font-normal">Invii automatici attivi (backup settimanale, avvisi)</Label>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={onSave} disabled={busy} data-testid="smtp-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva impostazioni</Button>
                <Button onClick={onTest} disabled={busy} variant="outline" data-testid="smtp-test-button" className="rounded-full">Invia email di prova</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
